/**
 * Hook for conversation mutation actions (rename, delete).
 *
 * Provides async functions that call the backend API and return
 * success/failure status for optimistic UI updates.
 */

import { useCallback, useState } from "react";
import { copilotApi } from "./useApi";
import type { ConversationSummary } from "../types";

export interface UseConversationActionsReturn {
  /** Rename a conversation. Returns the updated summary on success. */
  renameConversation: (id: string, title: string) => Promise<ConversationSummary | null>;
  /** Delete a conversation. Returns true on success. */
  deleteConversation: (id: string) => Promise<boolean>;
  /** Whether a mutation is in progress. */
  isMutating: boolean;
}

export function useConversationActions(): UseConversationActionsReturn {
  const [isMutating, setIsMutating] = useState(false);

  const renameConversation = useCallback(
    async (id: string, title: string): Promise<ConversationSummary | null> => {
      setIsMutating(true);
      try {
        const res = await copilotApi.patch<ConversationSummary>(
          `/copilot/conversations/${id}`,
          { title },
        );
        return res.data;
      } catch {
        return null;
      } finally {
        setIsMutating(false);
      }
    },
    [],
  );

  const deleteConversation = useCallback(async (id: string): Promise<boolean> => {
    setIsMutating(true);
    try {
      await copilotApi.delete(`/copilot/conversations/${id}`);
      return true;
    } catch {
      return false;
    } finally {
      setIsMutating(false);
    }
  }, []);

  return { renameConversation, deleteConversation, isMutating };
}
